import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, FileText } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { ReportViewer } from '@/components/report-viewer';

export function ProjectReportPage() {
  const { orgId, portfolioId, projectId } = useParams<{ orgId: string; portfolioId: string; projectId: string }>();
  const enabled = !!orgId && !!portfolioId && !!projectId;
  const basePath = `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}`;

  const { data: projectRes } = useQuery({
    queryKey: ['project', orgId, portfolioId, projectId],
    queryFn: () => apiClient.getProject(orgId!, portfolioId!, projectId!),
    enabled,
  });

  const { data: reportRes, isLoading, error } = useQuery({
    queryKey: ['project-report', orgId, portfolioId, projectId],
    queryFn: () => apiClient.get(`${basePath}/agents/report`),
    enabled,
  });

  const project = projectRes?.data as { name?: string } | undefined;
  const reportData = reportRes?.data as { report?: string } | undefined;
  const report = reportData?.report;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to={`${basePath}`}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Project
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Security Summary Report
        </h1>
        {project?.name && (
          <p className="text-muted-foreground mt-1">{project.name}</p>
        )}
      </div>

      {/* Report content */}
      {isLoading ? (
        <Card className="bg-card border border-border rounded-xl p-12 text-center">
          <Loader2 className="h-8 w-8 text-blue-700 dark:text-blue-400 animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Generating report...</p>
        </Card>
      ) : error ? (
        <Card className="bg-card border border-border rounded-xl p-8 text-center">
          <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Failed to load report. Run some agents first to generate a summary.
          </p>
          <Link
            to={`${basePath}/agents`}
            className="inline-block mt-4 text-sm text-blue-700 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Go to Agents
          </Link>
        </Card>
      ) : report ? (
        <ReportViewer
          report={report}
          title={(project?.name || 'Project') + ' Security Summary'}
        />
      ) : (
        <Card className="bg-card border border-border rounded-xl p-8 text-center">
          <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            No report available yet. Run agents to generate a project summary report.
          </p>
          <Link
            to={`${basePath}/agents`}
            className="inline-block mt-4 text-sm text-blue-700 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            Go to Agents
          </Link>
        </Card>
      )}
    </div>
  );
}
